import React, { useState } from 'react';
import useSimulationStore from '../../store/simulationStore';
import ChemicalFormula from './ChemicalFormula';
import {
  UnifiedControlPanel,
  UnifiedStatisticsPanel,
  UnifiedSpeciesPanel,
  UnifiedReactionsPanel,
  UnifiedElementsPanel,
} from './panels';
import {
  mainControlContainerStyle,
  mainToggleStyle,
  attachedMenuStyle,
  menuHeaderStyle,
  menuTitleStyle,
  menuCloseStyle,
  menuContentStyle,
} from './unifiedPanelStyles';

interface SidebarLayoutProps {
  particleStates: any[]; // Pass through for StatisticsPanel
}

const SidebarLayout: React.FC<SidebarLayoutProps> = ({ particleStates }) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const { uiFeatures } = useSimulationStore(state => ({
    uiFeatures: state.uiFeatures
  }));

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const renderUnifiedPanel = (panelType: string) => {
    // Render panels directly without wrapper containers
    switch (panelType) {
      case 'statistics':
        return uiFeatures.useNewStatistics ? (
          <UnifiedStatisticsPanel particleStates={particleStates} />
        ) : null;
      case 'species':
        return uiFeatures.useNewSpecies ? (
          <UnifiedSpeciesPanel />
        ) : null;
      case 'reactions':
        return uiFeatures.useNewReactions ? (
          <UnifiedReactionsPanel />
        ) : null;
      case 'elements':
        return uiFeatures.useNewSpecies ? (
          <UnifiedElementsPanel />
        ) : null;
      case 'controls':
        return uiFeatures.useNewControls ? (
          <UnifiedControlPanel />
        ) : null;
      default:
        return null;
    }
  };

  const getPanelIcon = (panel: string) => {
    switch (panel) {
      case 'statistics': return '📊';
      case 'reactions': return '🧪';
      case 'species': return '⚛️';
      case 'elements': return '🔬';
      case 'controls': return '🎛️';
      default: return '📋';
    }
  };

  const getPanelLabel = (panel: string) => {
    switch (panel) {
      case 'statistics': return 'Statistics';
      case 'reactions': return 'Reactions';
      case 'species': return 'Species';
      case 'elements': return 'Elements';
      case 'controls': return 'Controls';
      default: return 'Panel';
    }
  };

  const availablePanels = [
    { id: 'statistics' as const, enabled: uiFeatures.useNewStatistics },
    { id: 'reactions' as const, enabled: uiFeatures.useNewReactions },
    { id: 'species' as const, enabled: uiFeatures.useNewSpecies },
    { id: 'elements' as const, enabled: uiFeatures.useNewSpecies }, // Use same flag as species for now
    { id: 'controls' as const, enabled: uiFeatures.useNewControls },
  ].filter(panel => panel.enabled);

  // If no panels are enabled, don't render anything
  if (availablePanels.length === 0) {
    return null;
  }

  return (
    <>
      {/* Main Control Button and Menu */}
      <div style={mainControlContainerStyle}>
        <button
          style={mainToggleStyle}
          onClick={toggleExpanded}
          title={isExpanded ? 'Collapse' : 'Expand'}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.1)';
            e.currentTarget.style.boxShadow = '0 6px 25px rgba(79, 195, 247, 0.5)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 4px 20px rgba(79, 195, 247, 0.3)';
          }}
        >
          🧪
        </button>

        {/* Attached Menu */}
        {isExpanded && (
          <div style={attachedMenuStyle}>
            <div style={menuHeaderStyle}>
              <span style={menuTitleStyle}>NewReactor Controls</span>
              <button
                style={menuCloseStyle}
                onClick={() => setIsExpanded(false)}
                title="Close"
              >
                ×
              </button>
            </div>

            <div style={menuContentStyle}>
              {availablePanels.map(panel => (
                <div key={panel.id}>
                  {renderUnifiedPanel(panel.id)}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default SidebarLayout;
